import { Component, OnInit , Inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
   	FTEService,
   	ConditionChangeFTE,
   	ConditionChangeFTEDto,
   	ProgramArea
} from '../../../../domain';
import { FormBuilder, FormGroup, FormArray } from '@angular/forms';
import { ConsoleService } from '../../../../widgets/console.service';
import { combineLatest, from } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { GoogleAnalyticsService } from '../../../../main-core/google-analytics.service';
import { TooltipService } from '../../../../widgets/tooltip.service';

@Component({
	templateUrl: './condition-change-fte-modal.component.html',
	styleUrls: ['./condition-change-fte-modal.component.scss']
})
export class ConditionChangeFteModalComponent implements OnInit {
	public StaticText: any;
	Object = Object;
	form: FormGroup;
	formArray: FormArray;
	closing = false;
	programAreaNameById: {[id: number]: string} = {};
	_availableProgramAreas: {[controlIndex: number]: ProgramArea[]} = {};

	availableProgramAreas(programAreaControlIndex: number): ProgramArea[]{
		return this._availableProgramAreas[programAreaControlIndex] || [];
	}

	programAreaClass(disabled: boolean): string {
		return disabled ? "option-disabled" : "";
	}

	constructor(
		public dialogRef: MatDialogRef<ConditionChangeFteModalComponent>,
		private svc: FTEService,
		private snackBar: MatSnackBar,
		private console: ConsoleService,
		public tooltipSvc: TooltipService,
		private fb: FormBuilder,
		private gaService: GoogleAnalyticsService,
		@Inject(MAT_DIALOG_DATA) data: any)
	{ 
		this.StaticText = tooltipSvc.StaticText;
		{
			let programAreas = [];
			let f = <ConditionChangeFTE[]>data.conditionChangeFte;
			if(f){
				f.forEach(group =>
					programAreas.push(fb.group({
						programAreaId: group.programArea.id,
						fte: Math.round(group.fte.value * 100) /* From decimal to percentage.  Be sure to fix any rounding errors. */
					})));
			}

			this.formArray = fb.array(programAreas);
			this.form = fb.group({
				programAreas: this.formArray
			});

			if(programAreas.length == 0){
				this.addProgramAreaRow(null);
			}
		}

		const programAreas = from(this.svc.getProgramAreas());
		combineLatest([
			this.form.valueChanges.pipe(startWith(this.form.value)),
			programAreas
		]).subscribe(([formValue, programAreas]) => {
			/* This entire block is used to establish the programAreas that should be made available to each control.
			** Selections are mutually exclusive, meaning that when a control selects Program Area, that same Program Area is not available to another control. */
			let usedProgramAreaIds: {index:number, id: number}[] = 
				(<any[]>formValue['programAreas'])
				.map(pa => parseInt(pa.programAreaId))
				.map((id,index) => !!id ? {index,id} : undefined)
				.filter(id => !!id);

			(<FormArray>this.form.controls['programAreas']).controls.forEach((programAreaControl, programAreaControlIndex) => {
				let programAreaIdsUsedByOtherControls = 
					new Set<number>(
						usedProgramAreaIds
						.filter(p => p.index != programAreaControlIndex)
						.map(p => p.id));

				let currentProgramAreaID = formValue['programAreas'][programAreaControlIndex].programAreaId;

				this._availableProgramAreas[programAreaControlIndex] = 
					programAreas
						.filter(pa => !programAreaIdsUsedByOtherControls.has(pa.id) 
							&& (!pa.disabled // removes disabled Program areas 
								|| pa.id == currentProgramAreaID) // includes selected program area
							)
						.sort((a,b) => (a.disabled ? 1 : 0) - (b.disabled ? 1 : 0));
				});
		});

		programAreas.subscribe(pas => {
			let dict = {};
			pas.forEach(pa => dict[pa.id] = pa.name);
			this.programAreaNameById = dict;
		});
	}

	ngOnInit() {
	}

	cancel(){
		this.dialogRef.close();
	}

	save(){
		const dtos: ConditionChangeFTEDto[] = this.form.value.programAreas.map(
			group => new ConditionChangeFTEDto(group.programAreaId, (group.fte || 0) / 100));
		/* A null ProgramAreaID would cause a runtime error in the server. We musn't let them through. */
		let validDtos = dtos.filter(cc => !!cc.programAreaId);
		this.closing = true;
		this.svc.saveConditionChangeFTE(validDtos)
			.then(result => result.matchDo(
				_ => {
					this.snackBar.open('Condition Change FTE updated', null, {duration: 3000});
					this.gaService.eventEmitter("Condition Change FTE Updated", "Condition Change FTE", "Update");
					this.dialogRef.close('success');
				},
				errorMsg => {
					this.snackBar.open('Error: ' + errorMsg, null, {duration: 10000});
					this.closing = false;
				}))
			.catch(error => {
				this.snackBar.open(error.statusText, null, { duration: 10000 });
				this.closing = false;
			});
	}

	addProgramAreaRow(programAreaId?: number) {
		this.formArray.push(this.fb.group({
			programAreaId: programAreaId,
			fte: 0
		}));
	}

	get sum(): number {
		return this.form.value.programAreas.reduce((sum, group) =>
			sum + group.fte,
		   	0);
	}

	get validSum(): boolean {
		return this.sum == 100;
	}
}